import React, { FC, useMemo } from 'react';
import { Button } from 'shared/ui/Button';
import { Modal } from 'shared/ui/Modal';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useManageVerificationPhoto } from '../utils/hooks/useManageVerificationPhoto';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { TextArea } from 'shared/ui/TextArea';
import {
  TerminationDocumentRejectReason,
  TerminationDocumentType,
  VerificationDocumentRejectReason,
  VerificationDocumentType,
  VerificationFileStatus,
} from 'entities/VerificationPhoto';
import { useRole, UserPermissions } from 'entities/User';
import { useGetContractByIdQuery } from 'entities/Contract';
import dayjs from 'dayjs';
import { Select } from 'shared/ui/Select';
import { Form } from 'antd';
import { useCheckPermission } from 'shared/utils/hooks/useCheckPermission';

interface ManageVerificationButtonsProps {
  verificationPhotoId: string;
  documentType: VerificationDocumentType;
  fileStatus?: VerificationFileStatus;
  onSuccess?: () => void;
  contractId?: Nullable<string>;
}

export const ManageVerificationButtons: FC<ManageVerificationButtonsProps> = (props) => {
  const { verificationPhotoId, documentType, fileStatus, onSuccess, contractId } = props;

  const { isLoading, isRejectModalOpen, openRejectModal, closeRejectModal, handleApprove, handleReject, handleDelete } =
    useManageVerificationPhoto({ verificationPhotoId, onSuccess });

  const { data: contract } = useGetContractByIdQuery(contractId, { skip: !contractId });

  const { isMobile } = useScreenBreakpoints();
  const { isManagement } = useRole();
  const isForbidden = useCheckPermission(UserPermissions.CLOSE_CONTRACT_EXTENDED);
  const hasExtendedClosePermission = !isForbidden;
  const { t } = useAppTranslation('common');

  const [form] = Form.useForm();

  const isApproved = fileStatus === VerificationFileStatus.APPROVED;
  const isRejected = fileStatus === VerificationFileStatus.REJECTED;
  const isMoveOutDocumentType = documentType === TerminationDocumentType.MOVE_OUT_PHOTO;

  const isApproveDisabled = useMemo(() => {
    if (!contractId || hasExtendedClosePermission) {
      return false;
    }

    if (!contract?.noticeLeaveDate) {
      return true;
    }

    const noticeLeaveDate = dayjs(contract.noticeLeaveDate);

    if (!noticeLeaveDate.isValid()) {
      return true;
    }

    return noticeLeaveDate.isAfter(dayjs(), 'day');
  }, [contract?.noticeLeaveDate, contractId, hasExtendedClosePermission]);

  const isPending = fileStatus === VerificationFileStatus.PENDING || (!isApproved && !isRejected);

  const canDelete = hasExtendedClosePermission && isManagement ? true : isPending;
  const canApprove = !isApproved && isManagement && (hasExtendedClosePermission || isPending);
  const canReject = !isRejected && isManagement && (hasExtendedClosePermission || isPending);

  const rejectReasonOptions = Object.values(isMoveOutDocumentType ? TerminationDocumentRejectReason : VerificationDocumentRejectReason).map(
    (option) => ({
      label: t(option),
      value: option,
    }),
  );

  if (contract?.leaveDate) {
    return <></>;
  }

  return (
    <div className={`flex flex-col justify-center ${isMobile ? 'gap-2 mt-3' : 'gap-3 mt-4'}`}>
      {canDelete && (
        <Button onClick={handleDelete} theme="outlined" fontColor="text-error">
          {t('Delete', { ns: 'common' })}
        </Button>
      )}

      {!isApproved && isManagement && canApprove && (
        <Button onClick={handleApprove} isDisabled={isApproveDisabled}>
          {t('Approve', { ns: 'common' })}
        </Button>
      )}

      {!isRejected && isManagement && canReject && (
        <Button onClick={openRejectModal} theme="danger">
          {t('Reject', { ns: 'common' })}
        </Button>
      )}

      <Modal isOpen={isRejectModalOpen} onClose={closeRejectModal} width={1000}>
        <Form form={form} layout="vertical" onFinish={handleReject} className="flex flex-col">
          <div className="flex flex-col items-center justify-center gap-4 mb-4">
            <div className="text-primary text-center font-semibold">{t('Specify rejection reason')}</div>
          </div>
          <Form.Item label={t('Reason')} name="rejectReason" rules={[{ required: true, message: `${t('Please, select reason')}!` }]}>
            <Select options={rejectReasonOptions} placeholder={t('Select reason')} bordered />
          </Form.Item>
          <Form.Item label={t('Note')} name="note">
            <TextArea rows={4} placeholder={t('Enter note...')} bordered />
          </Form.Item>

          <div className="flex justify-end gap-3">
            <Button theme="secondary" onClick={closeRejectModal}>
              {t('Cancel', { ns: 'common' })}
            </Button>

            <Button type="submit" isLoading={isLoading}>
              {t('Confirm', { ns: 'common' })}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};
